/* oxlint-disable react/no-danger */

import clsx from 'clsx';
import { type FC } from 'react';

import { AdLabel } from 'shared/components/AdLabel';
import { MortgageAnnotations } from 'shared/components/MortgageAnnotations';
import { Ticker } from 'shared/components/Ticker';
import { toCssImageUrl } from 'shared/utils/toCssImageUrl';

import { type TGetMainpageBrandCompleteContextResult } from '../../utils/getMainpageBrandCompleteContext';

import styles from './MainpageBrandMobileRedesign.css';

export type TMainpageBrandMobileRedesignProps = {
  completeContext: TGetMainpageBrandCompleteContextResult;
};

export const MainpageBrandMobileRedesign: FC<TMainpageBrandMobileRedesignProps> = ({
  completeContext: {
    url,
    bgImageUrl,
    logoImageUrl,
    label,
    note,
    sanitizedActionText,
    mortgageWarning,
    mortgageTermsWarning,
    sizes,
    sanitizedName,
  },
}) => {
  const withMortgageAnnotations = !!(mortgageWarning || mortgageTermsWarning);
  const hasBothTexts = !!sanitizedActionText && !!sanitizedName;

  return (
    <a
      className={styles['container']}
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      style={{ ...sizes, backgroundImage: toCssImageUrl(bgImageUrl) }}
      data-testid="mainpage-brand-mobile-redesign"
    >
      {!!label && (
        <div className={styles['label']}>
          <AdLabel label={label} theme="deprecated" />
        </div>
      )}

      <div data-testid="content" className={clsx(styles['content'], !label && styles['content-no-label'])}>
        <div className={styles['content-inner']}>
          {!!logoImageUrl && (
            <div className={styles['logo']}>
              <img
                data-testid="logo"
                src={logoImageUrl}
                className={styles['logo-image']}
                alt={sanitizedActionText || sanitizedName}
              />
            </div>
          )}

          {(!!sanitizedActionText || !!sanitizedName) && (
            <div
              data-testid="content-texts"
              className={clsx(styles['content-texts'], hasBothTexts && styles['content-texts--both'])}
            >
              {!!sanitizedName && (
                <span
                  data-testid="content-text-name"
                  className={styles['content-text-name']}
                  dangerouslySetInnerHTML={{ __html: sanitizedName }}
                />
              )}

              {!!sanitizedActionText && (
                <span
                  data-testid="content-text-action"
                  className={styles['content-text-action']}
                  dangerouslySetInnerHTML={{ __html: sanitizedActionText }}
                />
              )}
            </div>
          )}
        </div>

        {!!note && (
          <div data-testid="disclaimer" className={styles['disclaimer']}>
            <Ticker text={note} theme="light" />
          </div>
        )}
      </div>

      {withMortgageAnnotations && (
        <div data-testid="mortgage-disclaimers-wrapper" className={styles['mortgage-disclaimer']}>
          <div className={styles['mortgage-disclaimer-inner']}>
            <MortgageAnnotations
              height={sizes.height}
              mortgageWarning={mortgageWarning}
              mortgageTermsWarning={mortgageTermsWarning}
            />
          </div>
        </div>
      )}
    </a>
  );
};
